// src/pages/Login.jsx

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithEmail, signInWithGoogle, signInAnon , sendOtpToPhone, verifyOtp } from "../services/firebaseAuthService"; // Firebase auth functions
import { useAuth } from "../context/AuthContext"; // Import useAuth to check authentication status
import { motion, AnimatePresence } from "framer-motion";
import PhoneInput, { getCountries, getCountryCallingCode} from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { isValidPhoneNumber, parsePhoneNumber,parsePhoneNumberFromString, AsYouType } from 'libphonenumber-js';
import { BlippLogo } from "../components/BlippLogo";



function LoginPage() {
  const { currentUser,isAnonymous } = useAuth();  // Access the current user from the AuthContext
  const [email, setemail] = useState("");
  const [password, setPassword] = useState("");
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("");
  const [step, setStep] = useState("email");
  const [otp, setOtp] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("US"); // Default to US
  const [fullPhone, setFullPhone] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const [resendTimer, setResendTimer] = useState(0);
  const [showRecaptcha, setShowRecaptcha] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  useEffect(() => {
    if (currentUser && !isAnonymous) {
      navigate("/chat");
    }
  }, [currentUser, isAnonymous, navigate]);

  useEffect(() => {
    if (popupMessage) {
      const timer = setTimeout(() => {
        setPopupMessage("");
      }, 2000); // 2000ms = 4 seconds

      return () => clearTimeout(timer); // Clear timer on unmount or when popupMessage changes
    }
  }, [popupMessage]);

  // Handle resend timer countdown
  useEffect(() => {
    if (step === "otp" && resendTimer > 0) {
      const interval = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [step, resendTimer]);
  

  const handleEmailContinue = (e) => {
    e.preventDefault();
    if (!email) {
      setEmailError("Email is required.");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setEmailError("");
    setPopupMessage("");
    setStep("password");
  };

  const handlePasswordContinue = async (e) => {
    e.preventDefault();
    if (!password) {
      setPasswordError("Password is required.");
      return;
    }
    setPasswordError("");
    try {
      await signInWithEmail(email, password);
      setPopupMessage("Login successful!");
      setPopupType("success");
      navigate("/chat");
    } catch (error) {
      setPasswordError("Incorrect email or password.");
      setPopupMessage("Login failed. Please check your credentials.");
      setPopupType("error");
    }
  };

  const handleGuestLogin = async () => {
    try {
      await signInAnon();
      setPopupMessage("Guest login successful!");
      setPopupType("success");
      navigate("/chat"); // Redirect after guest login
    } catch (error) {
      setPopupMessage("Guest login unavailable at the moment. Please try again later.");
      setPopupType("error");
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await signInWithGoogle();
      setPopupMessage("Google login successful!");
      setPopupType("success");
      navigate("/chat"); // Redirect after Google login
    } catch (error) {
      setPopupMessage("Google login unavailable at the moment. Please try again later.");
      setPopupType("error");
    }
  };

  const handleRegister = () => {
    setPopupType("info");
    setPopupMessage("Redirecting to register...");
    navigate("/register");  
  };

  const handleForgotPassword = () => {
    setPopupMessage("Redirecting to forgot password...");
    setPopupType("info");
    navigate("/forget");  
  };

const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!isPhoneValid) {
      setPopupMessage("Please enter a valid phone number.");
      setPopupType("error");
      return;
    }
    const phoneNumber = parsePhoneNumberFromString(fullPhone, selectedCountry);
    if (!phoneNumber) {
      setPopupMessage("Invalid phone number.");
      setPopupType("error");
      return;
    }
    const e164PhoneNumber = phoneNumber.format('E.164');
    try {
      // Try invisible reCAPTCHA first
      let result = await sendOtpToPhone(e164PhoneNumber, false);
      if (!result.success && result.message.includes("reCAPTCHA")) {
        // If invisible fails due to reCAPTCHA issues, show visible reCAPTCHA
        setShowRecaptcha(true);
        result = await sendOtpToPhone(e164PhoneNumber, true);
      }
      if (result.success) {
        setResendTimer(120);
        setStep("otp");
        setPopupMessage("OTP sent successfully.");
        setPopupType("success");
      } else {
        setPopupMessage(result.message || "Failed to send OTP.");
        setPopupType("error");
      }
    } catch (error) {
      setPopupMessage("Failed to send OTP. Please try again.");
      setPopupType("error");
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const cleanOtp = otp.replace(/\s/g, '');
    if (cleanOtp.length !== 6) {
      setPopupMessage("Please enter a valid 6-digit OTP.");
      setPopupType("error");
      return;
    }
    try {
      const result = await verifyOtp(cleanOtp);
      if (result.success) {
        setPopupMessage("Login successful!");
        setPopupType("success");
        navigate("/chat");
      } else {
        setPopupMessage(result.message || "Invalid OTP.");
        setPopupType("error");
      }
    } catch (error) {
      setPopupMessage("Failed to verify OTP. Please try again.");
      setPopupType("error");
    }
  };

  const handleResendOtp = async (e) => {
    e.preventDefault();
    if (resendTimer > 0) return;
    const phoneNumber = parsePhoneNumberFromString(fullPhone, selectedCountry);
    if (!phoneNumber) {
      setPopupMessage("Invalid phone number.");
      setPopupType("error");
      return;
    }
    const e164PhoneNumber = phoneNumber.format('E.164');
    try {
      // Try invisible reCAPTCHA first for resend
      let result = await sendOtpToPhone(e164PhoneNumber, false);
      if (!result.success && result.message.includes("reCAPTCHA")) {
        // If invisible fails, show visible reCAPTCHA
        setShowRecaptcha(true);
        result = await sendOtpToPhone(e164PhoneNumber, true);
      }
      if (result.success) {
        setResendTimer(120);
        setPopupMessage("OTP resent successfully.");
        setPopupType("success");
      } else {
        setPopupMessage(result.message || "Failed to resend OTP.");
        setPopupType("error");
      }
    } catch (error) {
      setPopupMessage("Failed to resend OTP. Please try again.");
      setPopupType("error");
    }
  };

// Handle country selection
  const handleCountryChange = (country) => {
    if (country) {
      setSelectedCountry(country);
      setFullPhone(""); // Reset phone number when country changes
      setIsPhoneValid(true);
    }
  };

  // Get the country code for display
  const countryCode = selectedCountry ? `+${getCountryCallingCode(selectedCountry)}` : "";

  const phoneChange = (e) => {
    const value = e.target.value;
    // Remove all non-digit characters
    const onlyNums = value.replace(/\D/g, '');
    setFullPhone(onlyNums);
    if (selectedCountry && value) {
      const phoneNumber = parsePhoneNumberFromString(value, selectedCountry);
      setIsPhoneValid(phoneNumber ? phoneNumber.isValid() : false);
    } else {
      setIsPhoneValid(true);
    }
  };


  return (
    <div className="bg-background min-h-screen">
      <div className="container mx-auto px-6 py-12 max-w-md">
        {/* AnimatePresence wrapper around the popup */}
        <AnimatePresence>
          {popupMessage && (
            <motion.div
              key="popup"
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 100 }}
              transition={{ type: "spring", stiffness: 200, damping: 20 }}
              className={`fixed top-6 right-8 z-50 w-80 p-4 rounded-lg text-white shadow-lg
                ${
                  popupType === "success"
                    ? "bg-green-600"
                    : popupType === "info"
                    ? "bg-blue-600"
                    : "bg-red-600"
                }`}
            >
              {popupMessage}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Top center BlippAI branding */}
        <div className="text-center mb-8 flex items-center justify-center">
          <a href="/" aria-label="BlippAI home"><BlippLogo className="h-9 w-auto" /></a>
        </div>

        {/* Main heading */}
        <h2 className="text-3xl font-semibold text-foreground text-center mb-8">
          {step === "email" || step === "phone"
            ? "Welcome back"
            : step === "password"
            ? "Enter your password"
            : step === "otp"
            ? "Enter your OTP"
            : ""}

        </h2>

        {step === "email" ? (
          <form onSubmit={handleEmailContinue} className="mb-6">
            <div className="mb-6">
              <label className="block text-sm font-medium text-primary mb-1" htmlFor="email">
                Email address
              </label>
              <input
                className="w-full px-4 py-3 bg-transparent border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={e => setemail(e.target.value)}
                required
                autoFocus
              />
              {emailError && (
                <p className="text-red-500 text-sm mt-1">{emailError}</p>
              )}
            </div>
            <button
              type="submit"
              className="w-full bg-primary text-primary-foreground py-3 rounded-md font-medium hover:bg-blue-400 transition-colors duration-300"
            >
              Continue
            </button>
          </form>
        ) : step === "password" ? (
          <form onSubmit={handlePasswordContinue} className="mb-6">
            <div className="mb-4 flex items-center justify-between border border-gray-700 rounded-md px-4 py-3 bg-gray-900">
              <span className="text-gray-400 text-sm">{email}</span>
              <button 
                type="button" 
                onClick={() => setStep("email")}
                className="text-primary hover:underline text-sm font-medium"
              >
                Edit
              </button>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-primary mb-1" htmlFor="password">
                Password
              </label>
              <div className="relative">
                <input
                  className="w-full px-4 py-3 bg-transparent border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent pr-12"
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                />
                <button 
                  type="button" 
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-300 hover:text-gray-100"
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                      <path d="M12 5C7 5 2.73 8.11 1 12c1.73 3.89 6 7 11 7s9.27-3.11 11-7c-1.73-3.89-6-7-11-7zm0 12c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" fill="currentColor"/>
                      <circle cx="12" cy="12" r="2.5" fill="currentColor"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                      <path d="M1 12C2.73 8.11 7 5 12 5c2.73 0 5.19.81 7.07 2.13M23 12c-1.73 3.89-6 7-11 7-2.73 0-5.19-.81-7.07-2.13M3.27 3.27l17.46 17.46" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9.53 9.53A3.5 3.5 0 0 0 12 16.5c.96 0 1.84-.38 2.47-1.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              </div>
              {passwordError && (
                <p className="text-red-500 text-sm mt-1">{passwordError}</p>
              )}
            </div>
            <button 
              type="button" 
              onClick={handleForgotPassword}
              className="text-primary hover:underline text-sm mb-4 block"
            >
              Forgot password ?
            </button>
            <button
              type="submit"
              className="w-full bg-primary text-primary-foreground py-3 rounded-md font-medium hover:bg-blue-400 transition-colors duration-300"
            >
              Continue
            </button>
          </form>
        ) : step === "phone" ? (
          <form onSubmit={handleSendOtp} className="mb-6 max-w-md mx-auto">
            {/* Country Code Dropdown */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-primary mb-1">
                Country
              </label>
              <select
                value={selectedCountry}
                onChange={(e) => handleCountryChange(e.target.value)}
                className="w-full px-4 py-3 bg-transparent text-sm font-medium [&>option]:bg-gray-900 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm text-gray-100"
              >
                {getCountries().sort((a, b) => {
                  const nameA = new Intl.DisplayNames(['en'], { type: 'region' }).of(a);
                  const nameB = new Intl.DisplayNames(['en'], { type: 'region' }).of(b);
                  return nameA.localeCompare(nameB);
                }).map((country) => {
                  const countryCode = `+${getCountryCallingCode(country)}`;
                  const countryName = new Intl.DisplayNames(['en'], { type: 'region' }).of(country);
                  return (
                    <option key={country} value={country}>
                      {`${countryName} (${countryCode})`}
                    </option>
                  );
                })}
              </select>
            </div>


            {/* Phone Number Input */}
                <div className="mb-6">
                  <label htmlFor="phone" className="block text-sm font-medium text-primary mb-1">
                    Phone number
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm font-medium ">
                      {countryCode}
                    </span>
                    <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={fullPhone}
                          onChange={phoneChange}
                          inputMode="numeric"
                          pattern="\d*"
                          className="w-full px-4 py-3 bg-transparent border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent "
                          style={{
                                  paddingLeft: `${countryCode.length * 10 + 20}px` // base 20 + 10px per character
                                }}
                          placeholder="Enter your phone number"
                        />
                  </div>
              {!isPhoneValid && (
                <p className="text-red-500 text-sm mt-1">Please enter a valid phone number.</p>
              )}
            </div>

              {/* reCAPTCHA container */}
              <div id="recaptcha-container" className={`mb-6 ${!showRecaptcha ? 'hidden' : ''}`}></div>

              {/* Continue button */}
              <button
                type="submit"
                className="w-full bg-primary text-primary-foreground py-3 rounded-md font-medium hover:bg-blue-400 transition-colors duration-300"
              >
                Continue
              </button>
          </form>


        ) : step === "otp" ? (
          <form onSubmit={handleVerifyOtp} className="mb-6">
            <div className="mb-4 flex items-center justify-between border border-gray-700 rounded-md px-4 py-3 bg-gray-900">
              <span className="text-gray-400 text-sm">
                {(() => {
                  try {
                    const fullNumber = `${countryCode} ${fullPhone}`;
                    const parsed = parsePhoneNumberFromString(fullNumber);
                    return parsed ? parsed.formatInternational() : `${countryCode} ${fullPhone}`;
                  } catch {
                    return `${countryCode} ${fullPhone}`;
                  }
                })()}
              </span>

              <button
                type="button"
                onClick={() => setStep("phone")}
                className="text-primary hover:underline text-sm font-medium"
              >
                Edit
              </button>
            </div>
            <div className="mb-6">
              <label htmlFor="otp" className="block text-sm font-medium text-primary mb-1 text-left">
                OTP
              </label>
              <input
                id="otp"
                type="text"
                inputMode="numeric"
                maxLength={7} // includes space or dash
                value={otp}
                onChange={(e) => {
                  let value = e.target.value.replace(/\D/g, ""); // Only digits
                  if (value.length > 6) value = value.slice(0, 6);
                  if (value.length > 3) {
                    value = `${value.slice(0, 3)} ${value.slice(3)}`;
                  }
                  setOtp(value);
                }}
                className="w-full px-6 py-3 border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-xl tracking-[0.5em] placeholder-gray-500 text-center"
                placeholder="000 000"
              />

              {/* Resend link below */}
              <button
                onClick={handleResendOtp}
                disabled={resendTimer > 0}
                className={`mt-3 text-sm font-medium ${resendTimer > 0 ? 'text-gray-400' : 'text-primary hover:underline'}`}
              >
                {resendTimer > 0 
                  ? `Resend in ${Math.floor(resendTimer / 60)}:${(resendTimer % 60).toString().padStart(2, '0')}` 
                  : 'Resend code'}
              </button>
            </div>

            <button
              type="submit"
              className="w-full bg-primary text-primary-foreground py-3 rounded-md font-medium hover:bg-blue-400 transition-colors duration-300"
            >
              Verify 
            </button>
          </form>
        ) : null }

        <p className="text-center text-sm text-gray-300 my-6">
          Don't have an account?{' '}
          <button
            type="button"
            onClick={handleRegister}
            className="text-primary hover:underline bg-transparent border-none cursor-pointer"
          >
            Sign up
          </button>
        </p>

        <div className="flex items-center my-8">
          <div className="flex-grow border-t border-gray-700"></div>
          <span className="px-4 text-sm text-gray-400">OR</span>
          <div className="flex-grow border-t border-gray-700"></div>
        </div>

        <div className="space-y-4">
          {step === "email" || step === "password" ? (
            // Show: Continue with Phone (when user is on email/password step)
            // <button
            //   type="button"
            //   onClick={() => setStep("phone")}
            //   className="w-full flex items-center justify-center px-4 py-3 border border-gray-700 rounded-md text-gray-200 hover:bg-gray-800 transition-colors duration-300"
            // >
            //   <svg className="w-5 h-5 mr-2 text-gray-300" viewBox="0 0 24 24" fill="none">
            //     <path fillRule="evenodd" clipRule="evenodd" d="M2 5.57143C2 3.59898 3.59898 2 5.57143 2H8.625C9.0287 2 9.39281 2.24274 9.54808 2.61538L11.4231 7.11538C11.5744 7.47863 11.4987 7.89686 11.2295 8.18394L9.82741 9.67954C10.9044 11.7563 12.2732 13.2047 14.3016 14.2842L15.7929 12.7929C16.0794 12.5064 16.5106 12.4211 16.8846 12.5769L21.3846 14.4519C21.7573 14.6072 22 14.9713 22 15.375V18.4286C22 20.401 20.401 22 18.4286 22C9.35532 22 2 14.6447 2 5.57143ZM5.57143 4C4.70355 4 4 4.70355 4 5.57143C4 13.5401 10.4599 20 18.4286 20C19.2964 20 20 19.2964 20 18.4286V16.0417L16.7336 14.6807L15.2071 16.2071C14.9098 16.5044 14.4582 16.584 14.0771 16.4062C11.0315 14.9849 9.12076 12.9271 7.71882 9.92289C7.54598 9.55251 7.61592 9.11423 7.89546 8.81606L9.32824 7.28777L7.95833 4H5.57143Z" fill="currentColor" />
            //   </svg>
            //   Continue with Phone
            // </button>
            null
          ) : (
            // Show: Continue with Email (when user is on phone/otp step)
            <button
              type="button"
              onClick={() => setStep("email")}
              className="w-full flex items-center justify-center px-4 py-3 border border-gray-700 rounded-md text-gray-200 hover:bg-gray-800 transition-colors duration-300"
            >
              <svg className="w-5 h-5 mr-2 text-gray-300" width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M2 7C2 5.34315 3.34315 4 5 4H19C20.6569 4 22 5.34315 22 7V17C22 18.6569 20.6569 20 19 20H5C3.34315 20 2 18.6569 2 17V7ZM4.26834 6.31832L11.4233 11.3689C11.769 11.6129 12.231 11.6129 12.5767 11.3689L19.7317 6.31832C19.5491 6.12247 19.2889 6 19 6H5C4.71114 6 4.45089 6.12247 4.26834 6.31832ZM20 8.57698L13.7301 13.0028C12.6929 13.735 11.3071 13.735 10.2699 13.0028L4 8.57698V17C4 17.5523 4.44772 18 5 18H19C19.5523 18 20 17.5523 20 17V8.57698Z" fill="currentColor"/>
              </svg>
              Continue with Email
            </button>
          )}
          <button
            type="button"
            onClick={handleGuestLogin}
            className="w-full flex items-center justify-center px-4 py-3 border border-gray-700 rounded-md text-gray-200 hover:bg-gray-800 transition-colors duration-300"
          >
            <svg className="w-5 h-5 mr-2 text-gray-300" fill="none" viewBox="0 0 24 24">
              <path fill="currentColor" d="M12 12c2.7 0 5-2.3 5-5s-2.3-5-5-5-5 2.3-5 5 2.3 5 5 5zm0 2c-3.3 0-10 1.7-10 5v3h20v-3c0-3.3-6.7-5-10-5z"/>
            </svg>
            Continue as guest
          </button>
          
          <button
            type="button"
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center px-4 py-3 border border-gray-700 rounded-md text-gray-200 hover:bg-gray-800 transition-colors duration-300"
          >
            <img 
              alt="Google logo" 
              className="w-5 h-5 mr-2" 
              src="data:image/svg+xml,%3Csvg width='48' height='48' viewBox='0 0 48 48' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg clipPath='url(%23clip0_17_40)'%3E%3Cpath d='M47.617 24.552c0-1.636-.146-3.2-.418-4.704H24.48v9.02h13.02c-.528 2.84-2.12 5.24-4.52 6.86v5.68h7.32c4.28-3.94 6.317-9.74 6.317-16.856z' fill='%234285F4'/%3E%3Cpath d='M24.48 48c6.12 0 11.24-2.04 14.987-5.56l-7.32-5.68c-2.04 1.36-4.66 2.16-7.667 2.16-5.9 0-10.9-3.98-12.687-9.32H4.97v5.84C8.7 43.98 16.02 48 24.48 48z' fill='%2334A853'/%3E%3Cpath d='M11.793 29.6A13.98 13.98 0 0 1 10.48 24c0-1.96.36-3.86 1.313-5.6v-5.84H4.97A23.98 23.98 0 0 0 .48 24c0 3.98.96 7.76 2.64 11.04l8.673-5.44z' fill='%23FBBC05'/%3E%3Cpath d='M24.48 9.52c3.34 0 6.32 1.14 8.68 3.38l6.48-6.48C35.72 2.36 30.6 0 24.48 0 16.02 0 8.7 4.02 4.97 10.16l8.673 5.84c1.787-5.34 6.787-9.32 12.687-9.32z' fill='%23EA4335'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0_17_40'%3E%3Cpath fill='%23fff' d='M0 0h48v48H0z'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E"
            />
            Continue with Google
          </button>
        </div>

        {/* <div className="mt-12 text-center text-xs text-gray-400">
          <button className="text-primary hover:underline bg-transparent border-none cursor-pointer">Terms of Use</button>
          <span className="mx-2">|</span>
          <button className="text-primary hover:underline bg-transparent border-none cursor-pointer">Privacy Policy</button>
        </div> */}
      </div>
    </div>
  );
}

export default LoginPage;
